import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import {
    Alert,
    Box,
    Checkbox,
    FormControlLabel,
    Menu,
    MenuItem,
    Button,
    Typography,
    Divider,
    Paper,
    Snackbar,
    Tooltip
} from '@mui/material';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow,
    TableContainer
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import WatchlistAdd from '@mui/icons-material/PlaylistAdd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from "@mui/material";
import { WATCHLIST_CONTAINER_WIDTH, CHART_SCREEN_TYPE, CHART_COLORS, VA_TYPE, WATCHLIST_MIN_WIDTH, WATCHLIST_MAX_WIDTH } from '../../../common/utility/mp_chart_constants';
import WatchlistDropdownComponent from '../../../components/AppHeader/WatchlistDropdownComponent';
import WatchlistSymbolModal from '../../../components/AppHeader/WatchlistInstrumentModal';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';
import { URL } from '../../../common/api/urls';
import { transformAndReverseMap } from '../../../common/utility/helperFunc';
import { MP_CHART_RELOAD_TIME, NOTIFICATION_POLLING_INTERVAL, SNACKBAR_AUTO_HIDE_DURATION_SHORT, TEXT_MSGS, WATCHLIST_TABLE_SETTINGS_KEY } from '../../../common/utility/constant';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../common/hooks/useAuth';
import useSyncUidataNotifyFlags from '../../../common/hooks/useSyncUidataNotifyFlags';
// ============================================================
// NEW - Centralized Permission System
// See: src/common/hooks/usePermissions.js
// ============================================================
import usePermissions from '../../../common/hooks/usePermissions';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { UserSettingsContext } from '../../../setup/routes-manager/RequireAuth';
import AppErrorPopup from '../../../components/AppErrorPopup';
import { WATCHLIST_MIN_DIVIDER_Y,WATCHLIST_DETAIL_PANEL_MIN } from '../../../common/utility/mp_chart_constants';
import { getWatchlistDividerY,saveWatchlistDividerY } from '../../../common/utility/helperFunc';
import EditIconRename from '@mui/icons-material/Edit';
import CreateWatchlistDialog from './create_watchlist';
import PlaceOrderModal from './place_order_modal';
import BrokerPopup from './broker_popup';
import { FEATURES } from '../../../common/utility/featurePermissions';
import BuySellIcon from '@mui/icons-material/Iso';

/** Axios/network failures with no HTTP response (e.g. ERR_CONNECTION_REFUSED). */
const isTransientNetworkError = (error) => {
    if (!error || error.response) return false;
    const code = error.code;
    const message = String(error.message ?? "");
    return (
        code === "ERR_NETWORK" ||
        code === "ECONNREFUSED" ||
        message.includes("Network Error") ||
        message.includes("ERR_CONNECTION_REFUSED")
    );
};

const OPT_INSTRUMENT_RE = /^O_[A-Z0-9.]+(?:CE|PE)\d{8}$/;

const isWatchlistPlaceOrderInstrument = (symbol) => (
  typeof symbol === 'string' && OPT_INSTRUMENT_RE.test(symbol)
);

const defaultSortModel = [];

// Sortable Row Component - makes table row draggable
const SortableRow = ({ id, row, columns, columnState, onRowClick, onRowDoubleClick, onDelete, onPlaceOrder, showPlaceOrder, selectedRow, locale, userSettings }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: String(id) });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    const isSelected = selectedRow?.symbol === row.symbol;

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={() => onRowClick(row)}
            onDoubleClick={() => onRowDoubleClick(row)}
            sx={{
                height: 30,
                backgroundColor: isSelected ? 'action.selected' : 'transparent',
                '&:hover': {
                    backgroundColor: 'action.hover',
                },
            }}
        >
            {columns.map((col) => {
                if (col.field === 'dragHandle') {
                    return (
                        <TableCell
                            key={col.field}
                            sx={{
                                width: col.width,
                                padding: '4px',
                                textAlign: col.align || 'left',
                                cursor: 'grab',
                                '&:active': {
                                    cursor: 'grabbing',
                                },
                            }}
                        >
                            <DragHandleCell />
                            
                        </TableCell>
                    );
                }
                if (col.field === 'actions') {
                    return (
                        <TableCell
                            key={col.field}
                            onPointerDown={(e) => e.stopPropagation()}
                            sx={{
                                width: col.width,
                                padding: 0,
                                textAlign: col.align || 'right',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: isSelected ? 'action.selected' : 'transparent',
                                zIndex: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {showPlaceOrder && isWatchlistPlaceOrderInstrument(row.symbol) ? (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onPlaceOrder(row);
                                    }}
                                    size="small"
                                    sx={{
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        'tr:hover &': { opacity: 1 },
                                    }}
                                >
                                    <BuySellIcon fontSize="small" sx={{ color: '#98bef7' }} />
                                </IconButton>
                            ) : null}
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(row);
                                }}
                                size="small"
                                sx={{
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    'tr:hover &': { opacity: 1 },
                                }}
                            >
                                <DeleteIcon fontSize="small" sx={{ color: "red" }} />
                            </IconButton>
                            </Box>
                        </TableCell>
                    );
                }

                const value = row[col.field];
                let displayValue = value;
                
                // Apply formatting based on column type
                if (col.renderCell) {
                    return (
                        <TableCell
                            key={col.field}
                            sx={{
                                width: columnState?.[col.field] || col.width,
                                padding: '4px 2px',
                                textAlign: col.align || 'right',
                                fontSize: '12px',
                            }}
                        >
                            {col.renderCell({ value, row })}
                        </TableCell>
                    );
                }

                return (
                    <TableCell
                        key={col.field}
                        sx={{
                            width: columnState?.[col.field] || col.width,
                            padding: '4px 2px',
                            textAlign: col.align || 'right',
                            fontSize: '12px',
                        }}
                    >
                        {displayValue}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

// Drag Handle Cell Component - displays the drag icon
const DragHandleCell = () => {
    return (
        <Box
            sx={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:active': {
                    cursor: 'grabbing',
                },
            }}
        >
            <DragIndicatorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Box>
    );
};

const Watchlist = (props) => {
    const theme = useTheme();
    // using the persisted width of the user
    const [width, setWidth] = useState(() => props.initialWidth ?? WATCHLIST_CONTAINER_WIDTH);
    const requiredCols = ['dragHandle', 'actions'];
    const isResizing = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    // const [selectedCols, setSelectedCols] = useState( allColumns.map((col) => col.field));
    const [selectedCols, setSelectedCols] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    
    const dragging = useRef(false);
    const dragStartY = useRef(0);
    const startDividerY = useRef(0);
    const [instrumentModal, setInstrumentModal] = useState(false);
    
    const [cancelUIData, responseDataUIData, errorUIData, loadedUIData, resetUIData, executeAPIUIData] = useAxiosWithAuth();
    const [cancelCreate, responseDataCreate, errorCreate, loadedCreate, resetCreate, executeAPICreate] = useAxiosWithAuth();
    const [cancel, responseData, error, loaded, reset, executeAPI] = useAxiosWithAuth();
    const watchlistLoadedOnceRef = useRef(false);
    const [renameError,setRenameError] = useState("")
    const { auth, setAuth } = useAuth();

    const getSavedWatchlist = () => {
        const saved = localStorage.getItem(WATCHLIST_TABLE_SETTINGS_KEY);

        if (!saved) {
            return undefined;
        }

        try {
        const parsed = JSON.parse(saved);
        if (parsed.auth?.email_id !== auth?.email_id) return undefined; 
        return parsed.selectedWatchList || undefined;
            } catch {
            return undefined;
        }
    };

    const [selectedWatchList, setSelectedWatchlist] = useState(getSavedWatchlist);
    const [selectedWatchListInstrument, setSelectedWatchlistInstrument] = useState();
    const [instrumentData, setInstrumentData] = useState([]);
    // const [dragStartY, setDragStartY] = useState(null);
    // const [startDividerY, setStartDividerY] = useState(null);
    const [columnList, setColumnList] = useState([]);
    const [tableData, setTableData] = useState([]);
    // Symbol that needs navigation after UIDATA adds it
    const [pendingSymbol, setPendingSymbol] = useState(null);
    // Drag and drop state
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(
        useSensor(PointerSensor, {
        activationConstraint: {
            distance: 2, // pixels of movement required before a drag is recognized
        },
    }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

  const syncUidataNotifyFlags = useSyncUidataNotifyFlags();
    // ============================================================
    // NEW - Centralized Permission System
    // See: src/common/utility/permissionService.js
    // ============================================================
    const permissions = usePermissions();
    const hasWatchlistAccess = permissions.canAccessWatchlist();
    const canPlaceBrokerOrder = permissions.canAccessFeature(FEATURES.BROKER_LOGIN);
    const isElite = permissions.isEliteUser();
    const [placeOrderRow, setPlaceOrderRow] = useState(null);
    const [showBrokerPopup, setShowBrokerPopup] = useState(false);
    const [orderToast, setOrderToast] = useState({ open: false, message: '' });

    const [state, setState] = useState({
        isLoading: true,
        isInstrumentLive: false,
        lastLoadTime: -1,
        refreshInterval: -1,
        isPollingAllowed: false,
        isTradingSessionActive: false,
        // OLD - Hardcoded FAST_DATA role and TBT_FEED resource check (DEPRECATED)
        // Replaced by: permissions.getChartReloadInterval()
        // reloadInerval: (auth.role.find(el => el == USER_ROLE_FIELDS.FAST_DATA)) ? (auth.resources.find(el => el == USER_RESOURCES_LIST.TBT_FEED) ? MP_CHART_RELOAD_TIME.FAST_DATA : MP_CHART_RELOAD_TIME.MINWISE_DATA) : (MP_CHART_RELOAD_TIME.SLOW_DATA),
        // NEW - Use centralized permission service
        reloadInerval: permissions.getChartReloadInterval(),
        pollingInterval: MP_CHART_RELOAD_TIME.POLLING_TIME,

    })
    const { isLoading, isInstrumentLive, lastLoadTime, reloadInerval, pollingInterval, refreshInterval, isPollingAllowed, isTradingSessionActive } = state;
    const stateRef = useRef(state);
    const navigate = useNavigate();
    const from = "/login";
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [sortModel, setSortModel] = useState(defaultSortModel);
    const { userSettings, setUserSettings, watchlists, refreshWatchlists, triggerWatchlistRefresh,setWatchlists } = useContext(UserSettingsContext);
    const { watchlistRefresh } = useContext(UserSettingsContext);
    const [dataTypeContracts, setDataTypeContracts] = useState(userSettings.data.data_in_contracts);

    const activeSort = sortModel?.[0] ?? null;
    const clampDividerY = (y) => {
        const maxY = window.innerHeight - 48 - WATCHLIST_DETAIL_PANEL_MIN;
        return Math.min(Math.max(y, WATCHLIST_MIN_DIVIDER_Y), maxY);
    };

    const [dividerY, setDividerY] = useState(() =>
        clampDividerY(getWatchlistDividerY(700))
    );
    //watchlist rename
    const [cancelRename, responseDataRename, errorRename, loadedRename, resetRename, executeAPIRename] = useAxiosWithAuth();
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameOldName, setRenameOldName] = useState();
    const [renameNewName, setRenameNewName] = useState();

// ref so the drag-end handler always reads the live value, not a stale closure
    const dividerYRef = useRef(dividerY);

    const handleRenameClick = (key) => {
    if (!key) return; // guard: header button is disabled when no selection, but be defensive
    setRenameOldName(key);
    setRenameError("");
    setRenameNewName(undefined)
    setRenameDialogOpen(true);
    };

    const renameWatchlist = (newName) => {
        const trimmedNewName = newName?.trim()
        const currentWatchlists = watchlists ?? {}
          const watchlistAlreadyExists = Object.keys(currentWatchlists).some(
        (name) =>
            name !== renameOldName &&
            name.toLowerCase() === trimmedNewName.toLowerCase()
    );

    if (watchlistAlreadyExists) {
        setRenameError(
            `Watchlist "${trimmedNewName}" already exists.`
        );
        return; 
    }
        setRenameError("")
        setRenameNewName(trimmedNewName);
        executeAPIRename(URL.CREATE_WATCHLIST, "POST", {
            name_update: true,
            old_name: renameOldName,
            new_name: trimmedNewName,
        });
    };

useEffect(() => {
    if (!loadedRename) return;

        if (responseDataRename != null) {

            // setMsgState({ openSnack: true, msg: TEXT_MSGS.WATCHLIST_RENAMED ?? "Watchlist renamed", severity: "success" });
            setRenameDialogOpen(false)
            setWatchlists?.((prev) => {
                const next = { ...(prev || {}) };
                if (renameOldName in next) {
                    next[renameNewName] = next[renameOldName];
                    delete next[renameOldName];
                }
                return next;
            });

            if (selectedWatchList === renameOldName) {
                setSelectedWatchlist(renameNewName);
                saveSelectedWatchlist(renameNewName);
            }

            refreshWatchlists?.();
            triggerWatchlistRefresh?.();
        } else if (errorRename !== null) {
            if (errorRename?.response?.status === 401 || errorRename?.response?.status === 403) {
                navigate(from, { replace: true });
            } else {
                setMsgState({ openSnack: true, msg: errorRename?.response?.data?.message ?? TEXT_MSGS.NETWORK_ERROR_MSG, severity: "error" });
            }
        }
        resetRename();
    
}, [loadedRename, responseDataRename,errorRename]);

    useEffect(() => { dividerYRef.current = dividerY; }, [dividerY]);

    useEffect(() => {
//if no watchlist access then whole useEffect will not run
    if(!hasWatchlistAccess) return;

    if (!selectedWatchList) return;
        console.log("Refresh triggered", watchlistRefresh);

    executeAPI( 
        `${URL.GET_WATCHLIST}?name=${selectedWatchList}&mdata=1`,
        "GET",
        {
            mdata: 1,
            name: selectedWatchList
        }
    );

}, [watchlistRefresh,hasWatchlistAccess]);

    useEffect(() => {
        console.log("userSettings changed =", userSettings.data.data_in_contracts);
        if (dataTypeContracts != userSettings.data.data_in_contracts) {
            setDataTypeContracts(userSettings.data.data_in_contracts)
            executeAPI(`${URL.GET_WATCHLIST}?name=${selectedWatchList}&mdata=1`, "GET", { mdata: 1, selectedWatchList })
        }
    }, [userSettings])
    // const [isLoading, setIsLoading] = useState(true);
    const [msgState, setMsgState] = useState({
        openSnack: false,
        msg: '',
        severity: 'info',
    })
    const { openSnack, msg, severity } = msgState
    const handleClose = () => {
    setMsgState((prev) => ({
        ...prev,
        openSnack: false,
        }));
    };

    const [columnState, setColumnState] = useState({});

    const handleColumnResize = (params) => {

        const newWidths = {
            ...columnState,
            [params.colDef.field]: params.width,
        };
        setColumnState(newWidths);
       
        saveSettings(newWidths, sortModel, selectedCols)
    };

    useEffect(() => {
        console.log("Column state=", columnState, selectedCols, sortModel)

    }, [columnState, selectedCols, sortModel])


    //polling or live chart update handler
    useEffect(() => {
        //set the ref to current state
        stateRef.current = state;

        const refreshInterval = setInterval(() => {

            if (selectedWatchList && stateRef.current.isPollingAllowed) {
                console.log("Calling polling notigication api")
                executeAPI(`${URL.GET_WATCHLIST}?name=${selectedWatchList}&mdata=1`, "GET", { mdata: 1, selectedWatchList })
            }

        }, stateRef.current.refreshInterval);

        return () => {
            clearInterval(refreshInterval);
        };
    }, [state]);


    useEffect(() => {
        const saved = localStorage.getItem(WATCHLIST_TABLE_SETTINGS_KEY);
        // console.log("Local storage data=", saved)
        if (saved) {
            const parsed = JSON.parse(saved);

        if (parsed.auth?.email_id === auth?.email_id) {
            setColumnState(parsed.columnWidths || {});
            setSortModel(parsed.sortModel || []);
            setSelectedCols(parsed.selectedCols || []);
        } else {
            // console.log("Different user - ignoring saved settings");

            setColumnState({});
            setSortModel(defaultSortModel);
            setSelectedCols([]);
        }
        } else {
            //   setColumnWidths(defaultColumnWidths);
            setSortModel(defaultSortModel);
        }
        let data = {
            "symbol": "NIFTY",
            "watchlist_instruments": 1,
            "additional_instruments": props.symbolList
        }
        executeAPIUIData(URL.MPCHART_UIDATA, "POST", data);
    }, [])

    useEffect(() => {
        if (loadedUIData) {
            // console.log("Require auth:loaded user settings ========>",responseData);
            if (responseDataUIData != null) {
                responseDataUIData.wl_instruments = responseDataUIData.wl_instruments.map((item) =>
                    transformAndReverseMap(item)
                );
                // const filteredData = responseDataUIData.instruments.filter(item => item.of == true)
                const filteredData = responseDataUIData.wl_instruments;
                console.log("filteredData watchlist length", filteredData.length)
                setInstrumentData(filteredData)
                syncUidataNotifyFlags(responseDataUIData);

                // localStorage.setItem('UIData', JSON.stringify(responseDataUIData));



            } else if (errorUIData !== null) {

            }
            if (pendingSymbol) {
                const found = responseDataUIData?.wl_instruments?.find(it => (it.symbol ?? it.instr) === pendingSymbol || it.instr === pendingSymbol);
                if (found) {
                    // now safe to navigate
                    props.reloadWithSymbol({symbol: pendingSymbol});
                    setPendingSymbol(null);
                }
            }
        }

    }, [loadedUIData, responseDataUIData])

    const columns = useMemo(
        () => columnList.filter((col) => selectedCols.includes(col.field)),
        [selectedCols, columnList]
    );

    const menuColumns = useMemo(
        () => columnList.filter((col) => !['displayName', 'actions', 'dragHandle'].includes(col.field)),
        [columnList]
    );

    // useEffect(() => {
    //     if (dragging) {
    //         const handleMouseMove = (e) => {
    //             if (dragging) {
    //                 const delta = e.clientY - dragStartY;
    //                 setDividerY(startDividerY + delta);
    //             }
    //         };

    //         const handleMouseUp = () => {
    //             setDragging(false);
    //         };

    //         window.addEventListener('mousemove', handleMouseMove);
    //         window.addEventListener('mouseup', handleMouseUp);

    //         return () => {
    //             window.removeEventListener('mousemove', handleMouseMove);
    //             window.removeEventListener('mouseup', handleMouseUp);
    //         };
    //     }
    // }, [dragging]);
// useEffect(() => {
//     if (dragging) {
//         const handleMouseMove = (e) => {
//             if (dragging) {
//                 const delta = e.clientY - dragStartY;
//                 setDividerY(clampDividerY(startDividerY + delta));
//             }
//         };

//         const handleMouseUp = () => {
//             setDragging(false);
//             saveWatchlistDividerY(dividerYRef.current);
//         };

//         window.addEventListener('mousemove', handleMouseMove);
//         window.addEventListener('mouseup', handleMouseUp);

//         return () => {
//             window.removeEventListener('mousemove', handleMouseMove);
//             window.removeEventListener('mouseup', handleMouseUp);
//         };
//     }
// }, [dragging]);


    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const toggleColumn = (field) => {
        // Prevent hiding required columns
        if (requiredCols.includes(field)) {
            return;
        }

        const newCols = selectedCols.includes(field)
            ? selectedCols.filter((f) => f !== field)
            : [...selectedCols, field];

        setSelectedCols(newCols);
      
        saveSettings(columnState, sortModel, newCols);

    };

    // const handleMouseDown = (e) => {
    //     setDragStartY(e.clientY);
    //     setStartDividerY(dividerY);
    //     setDragging(true);
    // };

const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation()
    dragging.current = true;
    dragStartY.current = e.clientY;
    startDividerY.current = dividerYRef.current;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';

  window.addEventListener("pointermove", handleMouseMove);
window.addEventListener("pointerup", handleMouseUp);
};

    const handleAddInstrument = () => {
        setInstrumentModal(true);
    }

    const watchListChangehandler = (name, list) => {
        if (name) {
            watchlistLoadedOnceRef.current = false;
            console.log("Watchlist mdata name=", name, list)
            setSelectedWatchlist(name);
            saveSelectedWatchlist(name)
            setSelectedWatchlistInstrument(list);
            executeAPI(`${URL.GET_WATCHLIST}?name=${name}&mdata=1`, "GET", { mdata: 1, name: name })
        } else {
            watchlistLoadedOnceRef.current = false;
            setSelectedWatchlist();
            setSelectedWatchlistInstrument([]);
            setColumnList([]);
            setTableData([]);
            setSelectedRow(null);

        }
    }
    

    const handleDelete = (deletedRow, list = selectedWatchListInstrument) => {
        // setRows((prev) => prev.filter((row) => row.id !== id));
        console.log("Deleteclicked ", selectedWatchListInstrument, list, deletedRow);
        const updatedSymbols = list.filter(sym => sym !== deletedRow.symbol);
        console.log("Deleteclicked 1", updatedSymbols, list, deletedRow);
        if (selectedWatchList) {
            // setSelectedWatchlistInstrument(updatedSymbols);
            closeHandler(updatedSymbols, selectedWatchList)
        }
    };




    useEffect(() => {
        if (loaded) {
            // console.log("Watch list mdata========>", responseData.data);
            if (responseData != null) {
                // setIsLoading(false);
                if (responseData.data) {

                    const floatFields = responseData.float_vals || [];
                    const numFields = responseData.num_vals || [];
                    const colorFields = responseData.color_vals || [];
                    const displayFields = responseData.show_cols || [];
                    const lsFields = responseData.ls_vals || [];

                    const locale = userSettings.data.number_format_locale;



                    //transform the metadata into list based on dynamic columns
                    let sampleRows = [];
                    let tempList = [];

                    // const selectedSymbols = responseData.data?.[selectedWatchList] || [];
                    // if (responseData.metadata) {
                    //     sampleRows = Object.entries(responseData.metadata)
                    //         .filter(([symbol]) => selectedSymbols.includes(symbol))
                    //         .map(([symbol, data], index) => {
                    //             const row = {
                    //                 id: index + 1,
                    //                 symbol,
                    //                 ...data,
                    //             };
                    //             tempList.push(symbol);

                    //             Object.entries(responseData.display_cols).forEach(([displayName, field]) => {
                    //                 let value = data[field] ?? null;

                    //                 // Check if isContract is true and field is in ls_vals, and data.ls exists and is not 0
                    //                 if (dataTypeContracts && responseData.ls_vals?.includes(field) && data.ls) {
                    //                     value = value !== null ? value / data.ls : null;
                    //                 }

                    //                 row[field] = value;
                    //             });

                    //             return row;
                    //         });
                    // }

                    //traversing from wathlist data instead of metadata
                    if (responseData.metadata && responseData.data?.[selectedWatchList]) {
                        const symbolsFromWatchlist = responseData.data[selectedWatchList];

                                                sampleRows = symbolsFromWatchlist
                                                    .filter((symbol) => responseData.metadata[symbol]) // ensure metadata exists
                                                    .map((symbol, index) => {
                                                        const data = responseData.metadata[symbol];
                                                        const row = {
                                                            id: index + 1,
                                                            symbol,
                                                            ...data,
                                                        };
                                                        tempList.push(symbol);

                                                        Object.entries(responseData.display_cols).forEach(([displayName, field]) => {
                                                            let value = data[field] ?? null;

                                                            // If it's a contract and ls is present and non-zero, divide
                                                            if (dataTypeContracts && responseData.ls_vals?.includes(field) && data.ls) {
                                                                value = value !== null ? (value / data.ls).toFixed() : null;
                                                            }

                                                            row[field] = value;
                                                        });

                                                        return row;
                                                    });
                      }
                      
                    setSelectedWatchlistInstrument(tempList);

                    const allColumns = [
                        {
                            field: 'dragHandle',
                            headerName: '',
                            sortable: false,
                            filterable: false,
                            width: 30,
                            disableColumnMenu: true,
                            align: "left",
                            cellClassName: 'drag-handle-column',
                            headerClassName: 'drag-handle-column',
                            renderCell: () => (
                                <DragHandleCell />
                            ),
                        },
                        ...Object.entries(responseData.display_cols).map(([headerName, field]) => {
                            const isFloat = floatFields.includes(field);
                            const isNumVal = numFields.includes(field);
                            const isColorField = colorFields.includes(field);
                            const isDisplay = displayFields.includes(field);

                            return {
                                field,
                                headerName,
                                headerAlign: 'center',
                                sortable: false,
                                // width: 80,
                                width: headerName == "Symbol" ? 115 : 75,
                                align: headerName === "Symbol" ? "left" : "right",

                                renderCell: (params) => {
                                    let value = params.value;

                                    // Format float
                                    if (isFloat && value != null && value !== '') {
                                        value = parseFloat(value).toFixed(2);
                                    }

                                    // Format number with locale
                                    if (isNumVal && value != null && value !== '') {
                                        value = Number(value).toLocaleString(locale);
                                    }

                                    // Decide color if it's a colorField
                                    let color = 'inherit';
                                    if (isColorField) {
                                        const numericValue = parseFloat(params.value);
                                        color = numericValue >= 0 ? 'green' : numericValue < 0 ? 'red' : 'inherit';
                                    }

                                    return (
                                        <Tooltip title={headerName == "Symbol" ? value : ""} placement='left'>
                                            <span style={{ fontSize: '12px', color, fontWeight: '600' }}>
                                                {value}
                                            </span>
                                        </Tooltip>
                                    );
                                }
                            };
                        }),
                        {
                            field: 'actions',
                            headerName: '',
                            sortable: false,
                            filterable: false,
                            width: canPlaceBrokerOrder ? 56 : 30,
                            disableColumnMenu: true,
                            // pinned: 'right',
                            align: "right",
                            cellClassName: 'delete-column',
                            headerClassName: 'delete-column',
                            renderCell: (params) => (

                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(params.row, tempList)
                                    }
                                    }
                                    size="small"
                                    sx={{
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        '.MuiDataGrid-row:hover &': { opacity: 1 }, // show on hover
                                    }}
                                >
                                    <DeleteIcon fontSize="small" sx={{ color: "red" }} />
                                </IconButton>

                            ),
                        },
                    ];
                    console.log("Session data 1=", columnList, selectedCols, allColumns.map((col) => col.field))
                    if (selectedCols && selectedCols.length == 0) {
                        const filteredCols = allColumns
                            .map((col) => col.field)
                            .filter((colField) => displayFields.includes(colField));
                        setSelectedCols(filteredCols);
                    }
                    // Ensure dragHandle and actions columns are always included
                    setSelectedCols(prev => {
                        const newCols = [...new Set([...requiredCols, ...(prev.length > 0 ? prev : allColumns.map((col) => col.field))])];
                        return newCols;
                    });

                    setColumnList(allColumns);
                    // console.log(
                    //     "Sorting from",
                    //     tableData.map(x => x.symbol)
                    // );
                    setTableData(sampleRows);
                    console.log("SelectedRows=", selectedRow, sampleRows, rowSelectionModel)
                    if (selectedRow) {
                        const matchedObject = sampleRows.find(item => item.symbol === selectedRow.symbol);

                        if (matchedObject)
                            setSelectedRow(matchedObject);
                        else {
                            setRowSelectionModel([]);

                            setSelectedRow(null);
                        }

                    }
                    // console.log("watchlist instrument in api call=", selectedWatchListInstrument);


                    let updateInterval = pollingInterval;
                    if (responseData.isPreOpenLive != undefined && responseData.isPreOpenLive) {
                        updateInterval = reloadInerval;
                    }
                    else if (responseData.isTradingSessionActive != undefined && responseData.isTradingSessionActive) {
                        updateInterval = MP_CHART_RELOAD_TIME.FAST_POLLING;
                    }
                    // else{
                    //     updateInterval = 5000;
                    // }
                    setState((prevState) => ({
                        ...prevState,
                        refreshInterval: updateInterval,
                        isPollingAllowed: true,
                        isTradingSessionActive: responseData.isTradingSessionActive,
                        isInstrumentLive: responseData.isInstrumentLive,
                        isLivePollingAllowed: responseData.isPreOpenLive,
                        isLoading: false
                    }));
                    console.log("Watchlist allcolumns=>", allColumns, sampleRows, updateInterval);
                    watchlistLoadedOnceRef.current = true;
                }

            } else if (error !== null) {
                setState((prevState) => ({
                    ...prevState,
                    isLoading: false
                }));

                console.log("Error data=", error);


                // setMsgState({open:true,msg:errorAutoSave?.response?.data?.message ?? `${TEXT_MSGS.NETWORK_ERROR_MSG}`,severity:"error"});
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    console.log("status received =", error?.response?.status)
                    navigate(from, { replace: true });
                } else if (
                    isTransientNetworkError(error) &&
                    watchlistLoadedOnceRef.current
                ) {
                    // Background poll failed (backend down / connection refused) — keep last data, no modal.
                    console.warn("Watchlist poll network error (popup suppressed):", error);
                } else {
                    setMsgState({ openSnack: true, msg: error?.response?.data?.message ?? `${TEXT_MSGS.NETWORK_ERROR_MSG}`, severity: "error" });
                }
            }
            reset();

        }

    }, [loaded, responseData, error])

    const closeHandler = (instrumentList, watchlistName) => {
        console.log("handleclose watchlist=", instrumentList, watchlistName)

        // setInstrumentModal(false);
        if (selectedWatchList) {
            setSelectedWatchlistInstrument(instrumentList);
            let data = {
                [selectedWatchList]: instrumentList
            }
            setState((prevState) => ({
                ...prevState,
                isPollingAllowed: false,
            }));
            executeAPICreate(URL.CREATE_WATCHLIST, "POST", data);
        }
    }

    useEffect(() => {
        if (loadedCreate) {
            // console.log("Require auth:loaded user settings ========>",responseData);
            if (responseDataCreate != null) {
                refreshWatchlists?.();
                triggerWatchlistRefresh?.();
                executeAPI(`${URL.GET_WATCHLIST}?name=${selectedWatchList}&mdata=1`, "GET", { mdata: 1, name: selectedWatchList })

            } else if (errorCreate !== null) {
                if (errorCreate?.response?.status === 401 || errorCreate?.response?.status === 403) {
                    console.log("status received =", errorCreate?.response?.status)
                    navigate(from, { replace: true });
                } else {
                    setMsgState({ openSnack: true, msg: errorCreate?.response?.data?.message ?? `${TEXT_MSGS.NETWORK_ERROR_MSG}`, severity: "error" });
                }
            }
        }

    }, [loadedCreate, responseDataCreate])

const handleMouseMove = (e) => {
    if (!dragging.current) return;

    const delta = e.clientY - dragStartY.current;

    setDividerY(clampDividerY(startDividerY.current + delta));
};

    useEffect(() => {
    props.onWidthChanged(hasWatchlistAccess ? width : 0);
    }, [width,hasWatchlistAccess])

    // const handleMouseUp = useCallback(() => {
    //     isResizing.current = false;
    //     document.removeEventListener('mousemove', handleMouseMove);
    //     document.removeEventListener('mouseup', handleMouseUp);
    // }, [handleMouseMove]);
const handleMouseUp = () => {
  dragging.current = false;
  saveWatchlistDividerY(dividerYRef.current);

  document.body.style.userSelect = '';
  document.body.style.cursor = '';

  window.removeEventListener("pointermove", handleMouseMove); // fixed
  window.removeEventListener("pointerup", handleMouseUp);     // fixed
};

const handleMouseUpWidth = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleMouseMoveWidth);
    document.removeEventListener('mouseup', handleMouseUpWidth);
};

const handleMouseMoveWidth = (e) => {
    if (!isResizing.current) return;
    const delta = e.clientX - startX.current;
    const newWidth = Math.min(Math.max(startWidth.current - delta, WATCHLIST_MIN_WIDTH), WATCHLIST_MAX_WIDTH);
    setWidth(newWidth);
};


const handleMouseDownWidth = (e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;

    document.addEventListener('mousemove', handleMouseMoveWidth);
    document.addEventListener('mouseup', handleMouseUpWidth);
};

    //merging the user updated with the current setting so in that way it will not update values which are not sent by the user
const saveSettings = (widths, sort, selectedCols) => {
    const existing = JSON.parse(
        localStorage.getItem(WATCHLIST_TABLE_SETTINGS_KEY) || "{}"
    );

    localStorage.setItem(
        WATCHLIST_TABLE_SETTINGS_KEY,
        JSON.stringify({
            ...existing,
            columnWidths: widths,
            sortModel: sort,
            selectedCols: selectedCols,
            auth:{
                email_id:auth.email_id,
            }
        })
    );
};

//function to save the user preference of wathclist
const saveSelectedWatchlist = (name) => {
    const existing = JSON.parse(
        localStorage.getItem(WATCHLIST_TABLE_SETTINGS_KEY) || "{}"
    );

    localStorage.setItem(
        WATCHLIST_TABLE_SETTINGS_KEY,
        JSON.stringify({
            ...existing,
            selectedWatchList: name,
            auth:{
                email_id:auth.email_id,
            }
        })
    );
};

    const handleSortModelChange = (model) => {
        // console.log("Savesettings=3",sortModel)
        setSortModel(model && model.length>0?model:sortModel);
        saveSettings(columnState, model, selectedCols);
    };

    const handleSortColumn = (field) => {
        if (field === 'symbol' || requiredCols.includes(field)) {
            return;
        }

        const isSameColumn = activeSort?.field === field;
        const nextSort = isSameColumn && activeSort.sort === 'asc' ? 'desc' : 'asc';
        const nextSortModel = [{ field, sort: nextSort }];

        setSortModel(nextSortModel);
        saveSettings(columnState, nextSortModel, selectedCols);
    };

    const getSortValue = (row, field) => {
        const value = row?.[field];

        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value === 'number') {
            return value;
        }

        if (typeof value === 'string') {
            const numericValue = Number(value.replace(/,/g, ''));
            if (!Number.isNaN(numericValue) && value.trim() !== '') {
                return numericValue;
            }

            return value.toLowerCase();
        }

        return value;
    };

    const sortedTableData = useMemo(() => {
        const data = [...tableData];

        if (!activeSort?.field || activeSort.field === 'symbol' || requiredCols.includes(activeSort.field)) {
            return data;
        }

        const direction = activeSort.sort === 'desc' ? -1 : 1;

        return data.sort((left, right) => {
            const leftValue = getSortValue(left, activeSort.field);
            const rightValue = getSortValue(right, activeSort.field);

            if (leftValue === null && rightValue === null) return 0;
            if (leftValue === null) return 1;
            if (rightValue === null) return -1;

            if (typeof leftValue === 'number' && typeof rightValue === 'number') {
                return (leftValue - rightValue) * direction;
            }

            return String(leftValue).localeCompare(String(rightValue)) * direction;
        });
    }, [tableData, activeSort]);

    // Drag and drop handlers
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        
        const { active, over } = event;
        
        if (!over || active.id === over.id) {
            setActiveId(null);
            return;
        }
        // displayData is sorted table data so suppose the activeSort is null then normal tableData
        // but if price is sorted ascending then it will use that sortedData
        const displayData = activeSort?.field ? sortedTableData : tableData;

        const oldIndex = displayData.findIndex((row) => String(row.symbol) === String(active.id));
        const newIndex = displayData.findIndex((row) => String(row.symbol) === String(over.id));
    
        if (oldIndex === -1 || newIndex === -1) {
            setActiveId(null);
            return;
        }

        const newTableData = arrayMove(displayData, oldIndex, newIndex);
        if (activeSort?.field) {
            setSortModel([]);
            saveSettings(columnState, [], selectedCols);
        }
        setTableData(newTableData);
      
        const newSymbolList = newTableData.map((row) => row.symbol);
        setSelectedWatchlistInstrument(newSymbolList);

        if (selectedWatchList) {
            let data = {
                [selectedWatchList]: newSymbolList
            };
            setState((prevState) => ({
                ...prevState,
                isPollingAllowed: false,
            }));
            executeAPICreate(URL.CREATE_WATCHLIST, "POST", data);
        }

        setActiveId(null);
    };

    if(!hasWatchlistAccess) return null;

    return (
        <Box
            sx={{
                flexGrow: 1,
                bgcolor: `${theme.palette.primaryTheme.shade02}`,
                borderLeft: '1px solid #ffffff',
                display: 'flex',
                flexDirection: 'column',
                width: `${width}px`,
                position: 'absolute',
                top: 'var(--dashboard-content-top, 48px)',
                left: `calc(100% - ${width + 50}px)`,
                overflow: 'hidden',
                // overflowY: 'auto',
                zIndex: 4,
                height: 'calc(100vh - var(--dashboard-content-top, 48px))',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width,
                    minWidth: WATCHLIST_MIN_WIDTH,
                    maxWidth: WATCHLIST_MAX_WIDTH,
                    height: '100vh',
                    //   backgroundColor: 'black',
                }}
            >
                <Box
                    onMouseDown={handleMouseDownWidth}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '3px',
                        cursor: 'ew-resize',
                        zIndex: 10,
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: '#ccc',
                        },
                    }}
                />
                {/* <Box p={2}>Left resizable box</Box> */}


                <Box flex={1} p={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1, justifyContent: 'space-between' }}>

                        <WatchlistDropdownComponent selectedWatchList={selectedWatchList}  onChange={(name, list) => watchListChangehandler(name, list)}  onRenameRequest={handleRenameClick} ></WatchlistDropdownComponent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Rename selected watchlist */}
                            <Tooltip
                                title={
                                    selectedWatchList
                                        ? `Rename ${selectedWatchList}`
                                        : "Select a watchlist"
                                }
                                placement="bottom"
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={!selectedWatchList}
                                        onClick={() => handleRenameClick(selectedWatchList)}
                                        sx={{
                                            color: "#ffffff",
                                            "&:hover": {
                                                backgroundColor: "#4278d7",
                                            },
                                        }}
                                    >
                                        <EditIconRename fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Add instruments" placement="bottom">
                                <span>
                                    <Button disabled={!selectedWatchList} onClick={handleAddInstrument} size="small" sx={{ width: "34px", minWidth: "34px" }}>
                                        <WatchlistAdd />
                                    </Button>
                                </span>
                            </Tooltip>
                            
                            <Button onClick={handleMenuClick} size="small" sx={{ width: "34px", minWidth: "34px" }}>
                                <MoreVertIcon />
                            </Button>
                        </Box>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            {menuColumns.map((col) => (
                                <MenuItem key={col.field} onClick={() => toggleColumn(col.field)}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={selectedCols.includes(col.field)} />
                                        }
                                        label={col.headerName}
                                    />
                                </MenuItem>
                            ))}
                        </Menu>

                    </Box>

                        <Box sx={{ height: dividerY,  width: `${width - 20}px`,display:"flex",flexDirection:"column" }}>
                            {instrumentData && instrumentData.length > 0 && tableData.length > 0 ?
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    autoScroll={false}

                                >
                                    <SortableContext
                                        items={sortedTableData.map((row) => String(row.symbol))}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <TableContainer  sx={{  flex: 1, minHeight: 0, overflow: 'auto', userSelect: 'none' }}>
                                            <Table stickyHeader size="small" sx={{ tableLayout: 'auto' }}>
                                                <TableHead>
                                                    <TableRow sx={{ height: 32 }}>
                                                        {columns.map((col) => (
                                                            <TableCell
                                                                key={col.field}
                                                                onClick={() => handleSortColumn(col.field)}
                                                                sx={{
                                                                    width: columnState?.[col.field] || col.width,
                                                                    minWidth: columnState?.[col.field] || col.width,
                                                                    padding: '4px 2px',
                                                                    textAlign: col.align || 'right',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    backgroundColor: theme.palette.primaryTheme.shade02,
                                                                    position: col.field === 'actions' ? 'sticky' : 'relative',
                                                                    right: col.field === 'actions' ? 0 : 'auto',
                                                                    zIndex: col.field === 'actions' ? 3 : 1,
                                                                    cursor: col.field === 'symbol' || requiredCols.includes(col.field) ? 'default' : 'pointer',
                                                                    userSelect: 'none',
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: col.align === 'left' ? 'flex-start' : 'flex-end', gap: 0.5 }}>
                                                                    <span>{col.headerName}</span>
                                                                    {activeSort?.field === col.field && !requiredCols.includes(col.field) && col.field !== 'symbol' ? (
                                                                        activeSort.sort === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                                                    ) : null}
                                                                </Box>
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sortedTableData.map((row) => (
                                                        <SortableRow
                                                            key={row.symbol}
                                                            id={row.symbol}
                                                            row={row}
                                                            columns={columns}
                                                            columnState={columnState}
                                                            onRowClick={(row) => setSelectedRow(row)}
                                                            onRowDoubleClick={(row) => {
                                                                console.log("instrument double click here", row, props)
                                                                if(!props.chartType || props.chartType === CHART_SCREEN_TYPE.FULL_CHART ){
                                                                    const exists = instrumentData?.some(
                                                                        (it) => (it.symbol ?? it.instr) === row.symbol || it.instr === row.symbol
                                                                    );
                                                                    if (!exists) {
                                                                        setPendingSymbol(row.symbol);
                                                                        executeAPIUIData(URL.MPCHART_UIDATA, "POST", {
                                                                            symbol: row.symbol,
                                                                            instruments: 1,
                                                                            watchlist_instruments: 1,
                                                                            additional_instruments: [row.symbol],
                                                                        });
                                                                    } else {
                                                                        props.reloadWithSymbol({symbol: row.symbol});
                                                                    }
                                                                    setSelectedRow(row);
                                                                } else {
                                                                    alert("action not supported for multi-window mode");
                                                                }
                                                            }}
                                                            onDelete={(row) => handleDelete(row)}
                                                            onPlaceOrder={(row) => setPlaceOrderRow(row)}
                                                            showPlaceOrder={canPlaceBrokerOrder}
                                                            selectedRow={selectedRow}
                                                            locale={userSettings.data.number_format_locale}
                                                            userSettings={userSettings}
                                                        />
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </SortableContext>
                                </DndContext> :
                                <></>
                            }
                        </Box>

                    <Box
                        onPointerDown={handleMouseDown}
                        sx={{
                            height: '8px',
                            cursor: 'row-resize',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover > div': { backgroundColor: theme.palette.primary.main },
                        }}
                    >
                        <Box sx={{ height: '2px', width: '100%', backgroundColor: '#ccc' }} />
                    </Box>

                    <Box sx={{ flexGrow: 1, p: 1, overflow: 'auto' }}>
                        {selectedRow ? (
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography variant="h6">{selectedRow.displayLongName}{" "} {selectedRow.series}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                        {canPlaceBrokerOrder && isWatchlistPlaceOrderInstrument(selectedRow.symbol) ? (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => setPlaceOrderRow(selectedRow)}
                                                sx={{
                                                    textTransform: 'none',
                                                    bgcolor: '#62a0ff',
                                                    '&:hover': { bgcolor: '#4f8df0' },
                                                }}
                                            >
                                                Place Order
                                            </Button>
                                        ) : null}
                                        <Tooltip title="Add active instrument to watchlist" placement="left">
                                            <span>
                                                <Button
                                                    disabled={!selectedWatchList}
                                                    onClick={handleAddInstrument}
                                                    size="small"
                                                    sx={{ width: '34px', minWidth: '34px', flexShrink: 0 }}
                                                >
                                                    <AddIcon />
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Typography variant="h5" mt={2}>
                                    {selectedRow.close ? selectedRow.close.toFixed(2) : selectedRow.close}
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            color: selectedRow.change < 0 ? 'red' : 'green',
                                            fontWeight: 'bold',
                                            //   fontFamily: 'monospace',
                                            fontSize: 16
                                        }}
                                    >
                                        {(selectedRow.change ? selectedRow.change.toFixed(2) : selectedRow.change)}
                                    </span>

                                </Typography>

                                <Typography mt={1} color="text.secondary">
                                    • Market {isInstrumentLive ? 'open' : 'closed'}
                                </Typography>
                                <Typography mt={2}>
                                    VPOC: {selectedRow.vpoc ? selectedRow.vpoc.toFixed(2) : 0}
                                    <br/>
                                    VWAP: {selectedRow.vwap ? selectedRow.vwap.toFixed(2) : 0}
                                    <br/><br/>
                                    {/* tbCOT (3/30): {selectedRow.tb3Cot ? selectedRow.tb3Cot : 0} / {selectedRow.tb30Cot ? selectedRow.tb30Cot : 0} */}
                                    {isElite? <>
                                    Alerts: {selectedRow.signal_ibis ? selectedRow.signal_ibis : 'None'}
                                    <br/>
                                    {selectedRow.imb_qty != undefined ?
                                    <>Imb Qty: {Number(selectedRow.imb_qty).toLocaleString(userSettings.data.number_format_locale)}
                                    <br/>
                                    </>:<></>}
                                    tbCOT (3/30): <span
                                        style={{
                                            marginLeft: 0,
                                            color: selectedRow.tb3Cot < 0 ? 'red' : 'green',
                                            fontWeight: 'bold',
                                            fontSize: 16
                                        }}
                                    >{selectedRow.tb3Cot ? Number(selectedRow.tb3Cot).toLocaleString(userSettings.data.number_format_locale) : 0}
                                    </span>
                                    &nbsp;/ <span
                                        style={{
                                            marginLeft: 0,
                                            color: selectedRow.tb30Cot < 0 ? 'red' : 'green',
                                            fontWeight: 'bold',
                                            fontSize: 16
                                        }}
                                    >{selectedRow.tb30Cot ? Number(selectedRow.tb30Cot).toLocaleString(userSettings.data.number_format_locale) : 0}
                                    </span>
                                    <br/>
                                    </> : <></> }
                                    Volumes: {selectedRow.vol ? Number(selectedRow.vol).toLocaleString(userSettings.data.number_format_locale) : 0}
                                    <br/>
                                    OI (dOI): {selectedRow.oi ? Number(selectedRow.oi).toLocaleString(userSettings.data.number_format_locale) : 0} (<span
                                        style={{
                                            marginLeft: 0,
                                            color: selectedRow.doi < 0 ? 'red' : 'green',
                                            fontWeight: 'bold',
                                            fontSize: 16
                                        }}
                                    >{selectedRow.doi ? Number(selectedRow.doi).toLocaleString(userSettings.data.number_format_locale) : 0}
                                    </span>)
                                    <br/><br/>
                                    Delta: {selectedRow.delta ? selectedRow.delta.toFixed(4) : 0}
                                    <br/>
                                    Gamma: {selectedRow.gamma ? selectedRow.gamma.toFixed(4) : 0}
                                </Typography>
                            </Paper>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Select a row to see details
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            {instrumentModal ?
                <WatchlistSymbolModal onCancel={() => setInstrumentModal(false)} selectedWatchListInstrument={selectedWatchListInstrument} name={selectedWatchList} open={instrumentModal} onClose={(list, name) => closeHandler(list, name)} instrumentList={instrumentData} /> :
                <></>
            }
            <AppErrorPopup
                open={openSnack}
                message={msg}
                severity={severity}
                onClose={handleClose}
                showCloseX
            />
            <PlaceOrderModal
                open={Boolean(placeOrderRow)}
                instrument={placeOrderRow}
                onClose={() => setPlaceOrderRow(null)}
                onOpenBrokerLogin={() => {
                    setPlaceOrderRow(null);
                    setShowBrokerPopup(true);
                }}
                onOrderSuccess={(message) => {
                    setOrderToast({ open: true, message: message || 'Order placed' });
                }}
                onOrderError={(message) => {
                    setMsgState({
                        openSnack: true,
                        msg: message,
                        severity: 'error',
                    });
                }}
            />
            <Snackbar
                open={orderToast.open}
                onClose={() => setOrderToast({ open: false, message: '' })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION_SHORT}
                sx={{ top: '48px', zIndex: 99999 }}
            >
                <Alert severity="success" sx={{ width: 'auto' }}>
                    {orderToast.message}
                </Alert>
            </Snackbar>
            {showBrokerPopup ? (
                <BrokerPopup
                    open={showBrokerPopup}
                    onClose={() => setShowBrokerPopup(false)}
                />
            ) : null}
        {renameDialogOpen ?
        <CreateWatchlistDialog
            title={"Rename Watchlist"}
            name={renameOldName}
            isRename
            open={true}
            error={renameError}
            cancelHandler={() => {
                setRenameDialogOpen(false);
                setRenameOldName(undefined);
                setRenameNewName(undefined);
                setRenameError("")
            }}
            onClearError={()=>setRenameError("")}
            saveHandler={(newName) => renameWatchlist(newName)}
            onSetError={(message)=>setRenameError(message)}
        /> :
        <></>
        }
        </Box>
    );
};

export default Watchlist;


